import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle, Spinner } from "@phosphor-icons/react";
import { fetchBillingPlans, createPlanCheckout } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DASHBOARD } from "@/constants/testIds";
import { toast } from "sonner";

const FEATURE_ROWS = [
  { key: "apps", label: "Apps / Products" },
  { key: "campaigns_per_month", label: "Campaigns / month" },
  { key: "renders_per_month", label: "Video renders / month" },
  { key: "ai_video", label: "AI video clips" },
  { key: "client_report", label: "Client Report PDF" },
];

const fmt = (plan, key) => {
  const v = plan[key];
  if (key === "ai_video" || key === "client_report") return v ? "Yes" : "No";
  return v === null || v === undefined ? "Unlimited" : v;
};

const PricingDialog = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    if (open) fetchBillingPlans().then(setPlans).catch(() => {});
  }, [open]);

  const handleSubscribe = async (planId) => {
    setCheckingOut(planId);
    try {
      const { checkout_url } = await createPlanCheckout(planId);
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setCheckingOut(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={DASHBOARD.pricingDialog} className="bg-[#0F0F0F] border-white/10 text-white max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Choose Your Plan</DialogTitle>
          <DialogDescription className="text-white/50 text-sm">Upgrade to unlock more products, campaigns, AI video clips, and client reports.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
          {plans.map((plan) => {
            const isCurrent = (user?.plan || "basic") === plan.id;
            return (
              <div
                key={plan.id}
                data-testid={DASHBOARD.pricingPlanCard(plan.id)}
                className={`rounded-2xl border p-5 flex flex-col ${isCurrent ? "border-[#F97316] bg-[#F97316]/5" : "border-white/10 bg-white/5"}`}
              >
                <p className="text-xs uppercase tracking-wider text-white/40">{plan.label}</p>
                <p className="font-display text-3xl font-bold mt-1 mb-4">
                  ${plan.price}
                  <span className="text-sm text-white/40">/mo</span>
                </p>
                <ul className="space-y-2 text-xs text-white/60 flex-1">
                  {FEATURE_ROWS.map((r) => (
                    <li key={r.key} className="flex items-center gap-1.5">
                      <CheckCircle size={12} className="text-emerald-400 shrink-0" /> {r.label}: {fmt(plan, r.key)}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={DASHBOARD.pricingSubscribeBtn(plan.id)}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrent || checkingOut === plan.id}
                  className={`mt-4 w-full rounded-full text-sm font-semibold py-2.5 flex items-center justify-center gap-2 disabled:opacity-50 ${
                    isCurrent ? "bg-white/10 text-white/40" : "bg-[#F97316] text-black hover:bg-[#FB923C]"
                  }`}
                >
                  {checkingOut === plan.id ? <Spinner size={14} className="animate-spin" /> : null}
                  {isCurrent ? "Current Plan" : `Subscribe to ${plan.label}`}
                </button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PricingDialog;
